"use client";

import { useTranslation } from 'react-i18next';
import { 
  List, 
  CornerDownLeft, 
  Copy, 
  Phone, 
  ExternalLink,
  PhoneCall,
  AlertCircle
} from 'lucide-react';

export function SampleListPreview() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full h-full">
      {/* WhatsApp-style background */}
      <div 
        className="absolute inset-0 rounded-lg overflow-hidden"
        style={{
          backgroundColor: '#f5f1eb'
        }}
      >
        {/* Background pattern with opacity */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'url("https://storage.googleapis.com/stark-media/wa-bg-2.png")',
            backgroundRepeat: 'repeat',
            backgroundSize: 'cover'
          }}
        ></div>
      </div>

      {/* Content container */}
      <div className="relative z-10 p-4 h-full min-h-[400px]">
        
        {/* Content container */}
        <div className="relative z-10">
          {/* Sample Notice */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-blue-700">
                  <div className="font-medium mb-1">{t('preview.samplePreviewTitle')}</div>
                  <div>{t('preview.sampleListMessageDescription')}</div>
                </div>
            </div>
          </div>

          {/* Message bubble */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden max-w-[280px] ml-auto relative">
            {/* Message tail */}
            <div className="absolute -right-2 top-3 w-0 h-0 border-l-8 border-l-white border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>

            {/* Message Content */}
            <div className="p-4 pb-2">
              <h3 className="font-medium text-gray-900 text-base whitespace-pre-wrap">
                {t('preview.sampleTitle')} {t('common.optional')}
              </h3>
              
              <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap mt-1">
                {t('preview.sampleDescription')} ({t('preview.required')}) - {t('preview.sampleDescriptionText')}
              </p>

              <p className="text-gray-500 text-sm whitespace-pre-wrap mt-1">
                {t('preview.sampleFooter')} {t('common.optional')} - {t('preview.sampleFooterText')}
              </p>
              
              {/* Timestamp */}
              <div className="text-right mt-1">
                <span className="text-xs text-gray-400">
                  12:34
                </span>
              </div>
            </div>

            {/* List Button */}
            <div className="border-t">
              <div className="flex items-center justify-center p-4 hover:bg-gray-50 cursor-pointer">
                <List className="w-5 h-5 mr-3 text-green-600" />
                  <span className="text-green-600 font-medium text-sm">{t('preview.viewOptions')}</span>
              </div>
            </div>

            {/* Expandable List Preview */}
            <div className="border-t bg-gray-50">
              <div className="p-3">
                <div className="text-xs text-gray-500 mb-2 font-medium">
                  {t('preview.sampleOptionsPreview')}:
                </div>
                <div className="space-y-2">
                  <div className="bg-white rounded p-2 text-xs">
                    <div className="font-medium">{t('preview.sectionName')}</div>
                    <div className="text-gray-600">• {t('preview.itemTitle')} - {t('preview.description')} {t('common.optional')}</div>
                    <div className="text-gray-600">• {t('preview.anotherItem')} - {t('preview.moreDetails')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SampleButtonPreview() {
  const { t } = useTranslation();
  return (
    <div className="relative w-full h-full">
      {/* WhatsApp-style background */}
      <div 
        className="absolute inset-0 rounded-lg overflow-hidden"
        style={{
          backgroundColor: '#f5f1eb'
        }}
      >
        {/* Background pattern with opacity */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'url("https://storage.googleapis.com/stark-media/wa-bg-2.png")',
            backgroundRepeat: 'repeat',
            backgroundSize: 'cover'
          }}
        ></div>
      </div>

      {/* Content container */}
      <div className="relative z-10 p-4 h-full min-h-[400px]">
        
        {/* Content container */}
        <div className="relative z-10">
          {/* Sample Notice */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-700">
                <div className="font-medium mb-1">{t('preview.samplePreviewTitle')}</div>
                <div>{t('preview.sampleButtonMessageDescription')}</div>
              </div>
            </div>
          </div>

          {/* Message bubble */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden max-w-[280px] ml-auto relative">
            {/* Message tail */}
            <div className="absolute -right-2 top-3 w-0 h-0 border-l-8 border-l-white border-t-4 border-t-transparent border-b-4 border-b-transparent"></div>

            {/* Media Preview Sample */}
            <div className="relative">
              <div className="aspect-video bg-gray-200 relative overflow-hidden flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <div className="text-xs">{t('preview.sampleMedia')} {t('common.optional')}</div>
                  <div className="text-xs">{t('preview.imageOrVideo')}</div>
                </div>
              </div>
            </div>

            {/* Message Content */}
            <div className="p-4 pb-2">
              <div className="text-gray-900 text-base font-medium whitespace-pre-wrap">
                {t('preview.sampleTitle')} ({t('preview.required')})
              </div>

              <div className="text-gray-500 text-sm mt-1 whitespace-pre-wrap">
                {t('preview.sampleFooterText')} {t('common.optional')}
              </div>
              
              {/* Timestamp */}
              <div className="text-right mt-1">
                <span className="text-xs text-gray-400">
                  12:34
                </span>
              </div>
            </div>

            {/* Sample Buttons */}
            <div className="border-t">
              <div className="border-b">
                <div className="flex items-center justify-center p-4 hover:bg-gray-50 cursor-pointer">
                  <CornerDownLeft className="w-5 h-5 mr-3 text-green-600" />
                  <span className="text-green-600 font-medium text-sm">{t('preview.quickReplyButton')}</span>
                </div>
              </div>
              <div className="border-b">
                <div className="flex items-center justify-center p-4 hover:bg-gray-50 cursor-pointer">
                  <ExternalLink className="w-5 h-5 mr-3 text-green-600" />
                  <span className="text-green-600 font-medium text-sm">{t('preview.visitWebsite')}</span>
                </div>
              </div>
              <div className="border-b">
                <div className="flex items-center justify-center p-4 hover:bg-gray-50 cursor-pointer">
                  <Phone className="w-5 h-5 mr-3 text-green-600" />
                  <span className="text-green-600 font-medium text-sm">{t('preview.callUs')}</span>
                </div>
              </div>
              <div className="border-b">
                <div className="flex items-center justify-center p-4 hover:bg-gray-50 cursor-pointer">
                  <PhoneCall className="w-5 h-5 mr-3 text-green-600" />
                  <span className="text-green-600 font-medium text-sm">{t('preview.voiceCallButton')}</span>
                </div>
              </div>
              <div className="border-b last:border-b-0">
                <div className="flex items-center justify-center p-4 hover:bg-gray-50 cursor-pointer">
                  <Copy className="w-5 h-5 mr-3 text-green-600" />
                  <span className="text-green-600 font-medium text-sm">{t('preview.copyCoupon')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Button types explanation */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-xs text-gray-600">
              <div className="font-medium mb-2">{t('preview.buttonTypesAvailable')}:</div>
              <div className="space-y-1">
                <div className="flex items-center space-x-2">
                  <CornerDownLeft className="w-3 h-3" />
                  <span>{t('preview.quickReplyDescription')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <ExternalLink className="w-3 h-3" />
                  <span>{t('preview.websiteDescription')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-3 h-3" />
                  <span>{t('preview.callDescription')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <PhoneCall className="w-3 h-3" />
                  <span>{t('preview.voiceCallDescription')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Copy className="w-3 h-3" />
                  <span>{t('preview.copyDescription')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}